import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { STORYLINE_LEGEND, STORYLINE_LEGEND_NOTE, storylinesCanvas, subgraphToGraph } from '../../lib/signal-graph'
import { signalNodeId, type PromotedSubgraph } from '../../lib/signal-subgraphs'
import { GraphErrorBoundary } from './force/GraphErrorBoundary'

/** The canvas chunk (force-graph) loads only when Graph mode mounts. */
const loadForceGraph = () => import('./force/StorylineForceGraph')
let ForceGraphView = lazy(loadForceGraph)
/** After a failed chunk: a fresh lazy component, so the import runs again. */
const retryForceGraph = () => {
  ForceGraphView = lazy(loadForceGraph)
}

/**
 * Storylines · Graph mode (decision S33). Every storyline on one force-graph
 * canvas (lazy chunk, `force/StorylineForceGraph`: sonar-hex signals, emitter
 * suspects, reticle targets; particles on the edges; wheel zoom, node drag;
 * a hidden DOM mirror for keyboard and names); hovering a node lights its
 * storyline and shows a Focus chip — the chip, a double-click or F on a node
 * focuses it (refit, ← back; the storyline's Kill Chain and the rest sit in
 * the docked pane's storyline panel). A signal node opens that signal in the
 * docked pane; an entity node only highlights its edges (Esc clears it
 * first, then the page leaves the focus). Props in, events out — `?sg` lives
 * in the page's URL.
 */
export interface StorylineGraphProps {
  storylines: readonly PromotedSubgraph[]
  /** ?sg= — unknown keys fall back to all (Review Focus 3). */
  focusKey: string | null
  openUuid: string | null
  onFocus: (key: string | null) => void
  onOpenSignal: (uuid: string, storyKey: string) => void
}

export function StorylineGraph({ storylines, focusKey, openUuid, onFocus, onOpenSignal }: StorylineGraphProps) {
  const focused = focusKey ? (storylines.find((s) => s.key === focusKey) ?? null) : null
  const all = useMemo(() => storylinesCanvas(storylines), [storylines])
  const one = useMemo(() => (focused ? subgraphToGraph(focused) : null), [focused])
  const graph = one ?? all.graph
  // Retry remounts the boundary on the fresh lazy component.
  const [attempt, setAttempt] = useState(0)

  // The entity highlight belongs to the view it was picked in: a focus
  // change from outside (URL, keyboard) or a node gone from the graph drops it
  // for good, so no edge stays dimmed and Esc is not swallowed by a stale pick.
  const view = focusKey ?? ''
  const [pick, setPick] = useState<{ id: string; view: string } | null>(null)
  const live = pick !== null && pick.view === view && graph.nodes.some((n) => n.id === pick.id)
  if (pick && !live) setPick(null) // render-time reset (React: adjusting state on a prop change)
  const picked = live ? pick.id : null
  const setPicked = (id: string | null) => setPick(id === null ? null : { id, view })

  const select = (id: string | null) => {
    if (id === null) return setPicked(null)
    if (id.startsWith('sig:')) {
      const node = graph.nodes.find((n) => n.id === id)
      if (node?.group) onOpenSignal(id.slice(4), node.group)
      return
    }
    setPicked(picked === id ? null : id)
  }
  const selected = picked ?? (openUuid ? signalNodeId(openUuid) : null)

  // Focusing remounts the canvas (and going back unmounts the back button), so keyboard focus
  // would fall to <body>: land it on ← All storylines after a focus, on the storyline's first node
  // after going back. Only when focus was lost or inside the graph — never stolen from elsewhere.
  const rootRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLButtonElement>(null)
  const focusedKey = focused?.key ?? null
  const prevKey = useRef(focusedKey)
  useEffect(() => {
    const prev = prevKey.current
    prevKey.current = focusedKey
    if (prev === focusedKey) return
    const active = document.activeElement
    if (active && active !== document.body && !rootRef.current?.contains(active)) return
    if (focusedKey) {
      backRef.current?.focus()
      return
    }
    const first = prev ? graph.nodes.find((n) => n.group === prev)?.id : undefined
    const el = [...(rootRef.current?.querySelectorAll<HTMLElement>('[data-node]') ?? [])].find((e) => e.getAttribute('data-node') === first)
    el?.focus()
  }, [focusedKey, graph])

  return (
    <div
      ref={rootRef}
      className="sg-graph"
      role="presentation"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && picked) {
          e.stopPropagation()
          setPicked(null)
        }
      }}
    >
      {focused ? (
        <div className="hud-row sg-graph-head">
          <button ref={backRef} type="button" className="bui-btn bui-btn-ghost bui-btn-sm" onClick={() => { setPicked(null); onFocus(null) }}>
            ← All storylines
          </button>
          <span className="sg-graph-title">{focused.title}</span>
        </div>
      ) : null}
      <GraphErrorBoundary key={attempt} onRetry={() => {
          retryForceGraph()
          setAttempt((a) => a + 1)
        }}>
        <Suspense
          fallback={
            <p className="hud-unit" role="status">
              Loading graph…
            </p>
          }
        >
          <ForceGraphView
            // Keyed on the view only: a re-rank (watch, promotion) or a refetch keeps the
            // instance, so a zoom / pan stays and surviving nodes keep their place.
            key={focused ? focused.key : 'all'}
            graph={graph}
            selected={selected}
            onSelect={select}
            {...(focused ? {} : { groups: all.groups, onFocusGroup: (k: string) => { setPicked(null); onFocus(k) } })}
            highlightEdgesOf={picked}
            legend={STORYLINE_LEGEND}
            legendNote={STORYLINE_LEGEND_NOTE}
            label={
              focused
                ? `Storyline graph — ${focused.title}`
                : 'Storylines graph — hover a node, then Focus (or double-click, or F) to focus its storyline; arrow keys move between nodes'
            }
          />
        </Suspense>
      </GraphErrorBoundary>
    </div>
  )
}
